/**
 * Phase 46 (2026-07-29) - Compact date + live-clock + refresh cluster for the
 * shared top bar. Only renders when the current route publishes a refresh
 * action via `useDashboardActions` (i.e. when we are on the Dashboard).
 */
import { useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";
import { useDashboardActions } from "@/context/DashboardActionsContext";
import { toast } from "sonner";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";

function fmtDateShort(d) {
  // e.g. "Wed, Jul 29"
  return d.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

function fmtClock(d) {
  // e.g. "12:23 PM"
  return d.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

export default function DashboardTopBarCluster() {
  const { refresh, refreshing } = useDashboardActions();
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    if (!refresh) return undefined;
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, [refresh]);

  // Only render on pages that have published a refresh action.
  if (!refresh) return null;

  const handleRefresh = async () => {
    try {
      await refresh();
      toast.success("Dashboard refreshed", { duration: 1600 });
    } catch (e) {
      toast.error("Refresh failed");
    }
  };

  return (
    <div
      className="flex items-center gap-2"
      data-testid="dashboard-topbar-cluster"
    >
      {/* Date pill - hidden on mobile (< md) */}
      <span
        className="hidden md:inline-flex items-center rounded-full border px-2.5 py-1 text-sm"
        style={{
          borderColor: "var(--border-default)",
          color: "var(--text-secondary)",
          backgroundColor: "var(--bg-surface)",
        }}
        data-testid="topbar-date-pill"
      >
        {fmtDateShort(now)}
      </span>
      {/* Live clock - desktop only (>= lg) */}
      <span
        className="hidden lg:inline-flex items-center gap-1.5 text-xs"
        style={{ color: "var(--text-muted)" }}
        data-testid="topbar-live-clock"
      >
        <span
          className="inline-block h-1.5 w-1.5 rounded-full"
          style={{ backgroundColor: "var(--success, #0E9F6E)" }}
        />
        <span className="tabular-nums">{fmtClock(now)}</span>
      </span>
      {/* Refresh icon-only button - always visible */}
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={handleRefresh}
              disabled={refreshing}
              aria-label="Refresh dashboard data"
              className="inline-flex h-8 w-8 items-center justify-center rounded-full hover:bg-slate-100 disabled:opacity-50 transition-colors"
              style={{ color: "var(--text-secondary)" }}
              data-testid="topbar-refresh-btn"
            >
              <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
            </button>
          </TooltipTrigger>
          <TooltipContent side="bottom" data-testid="topbar-refresh-tooltip">
            Refresh dashboard data
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
}
